import { z } from 'zod';

import { apiRequest } from '@/api/client';
import { resolveStorefrontHost } from '@/features/storefront/storefrontHost';
import { storefrontLanguageHeaders } from '@/features/storefront/storefrontLanguageHeader';

/**
 * The shop's physical branches, for the `store_locator` section.
 *
 * Mirrors `StorefrontStoreLocations` in `backend/app/schemas/store_location.py`.
 * Opening hours are wall-clock `HH:MM` strings in `timezone` (weekday 0 =
 * Monday), the bookings section's shape; an empty list means "not listed".
 */
const hoursSchema = z.object({
  weekday: z.number(),
  closed: z.boolean().default(false),
  opens: z.string().default('09:00'),
  closes: z.string().default('17:00'),
});

/** Decimals arrive as strings; the section only ever writes them into URLs. */
const coordinate = z
  .union([z.string(), z.number()])
  .nullable()
  .optional()
  .transform((value) => (value === null || value === undefined || value === '' ? null : String(value)));

export const storefrontLocationSchema = z.object({
  id: z.string(),
  name: z.string(),
  address_line1: z.string(),
  address_line2: z.string().nullable().optional(),
  city: z.string(),
  state: z.string().nullable().optional(),
  pincode: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  latitude: coordinate,
  longitude: coordinate,
  opening_hours: z.array(hoursSchema).default([]),
  services: z.array(z.string()).default([]),
});

export const storefrontLocationsSchema = z.object({
  timezone: z.string().default('Asia/Kolkata'),
  locations: z.array(storefrontLocationSchema).default([]),
});

export type StorefrontLocation = z.infer<typeof storefrontLocationSchema>;
export type StorefrontLocations = z.infer<typeof storefrontLocationsSchema>;
export type LocationHours = z.infer<typeof hoursSchema>;

export const NO_LOCATIONS: StorefrontLocations = { timezone: 'Asia/Kolkata', locations: [] };

/**
 * `GET /storefront/locations`. Public, like the rest of the storefront: no
 * merchant token travels with it, and the shop is named in a header.
 */
export function fetchStorefrontLocations(host?: string | null): Promise<StorefrontLocations> {
  const resolved = host ?? resolveStorefrontHost();
  return apiRequest('/storefront/locations', storefrontLocationsSchema, {
    cache: 'no-store',
    auth: 'none',
    headers: {
      ...(resolved ? { 'X-Storefront-Host': resolved } : {}),
      ...storefrontLanguageHeaders(),
    },
  });
}
